import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faCopy, faCheck, faTrash, faLink } from '@fortawesome/free-solid-svg-icons'
import type { PendingAccount, PendingAccountRole } from '../../types/admin'
import { Modal } from '../Modal'

interface PendingAccountsTableProps {
  accounts: PendingAccount[]
  onResendInvite: (id: string) => void
  onRemove: (account: PendingAccount) => void
}

function buildInviteUrl(token: string): string {
  return `${window.location.origin}/register?token=${token}`
}

const roleColors: Record<PendingAccountRole, string> = {
  ADMIN: 'bg-purple-100 text-purple-800',
  PARTNER: 'bg-blue-100 text-blue-800',
  CUSTOMER: 'bg-gray-100 text-gray-800',
  GUEST: 'bg-yellow-100 text-yellow-800',
}

const roleLabels: Record<PendingAccountRole, string> = {
  ADMIN: 'Admin',
  PARTNER: 'Partner',
  CUSTOMER: 'Customer',
  GUEST: 'Guest',
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function PendingAccountsTable({
  accounts,
  onResendInvite,
  onRemove,
}: PendingAccountsTableProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [linkAccount, setLinkAccount] = useState<PendingAccount | null>(null)

  const handleCopyLink = async (account: PendingAccount) => {
    if (!account.token) return
    try {
      await navigator.clipboard.writeText(buildInviteUrl(account.token))
      setCopiedId(account.id)
      setTimeout(() => setCopiedId((current) => (current === account.id ? null : current)), 2000)
    } catch (err) {
      console.error('Failed to copy invite link', err)
    }
  }

  if (accounts.length === 0) {
    return <div className="text-center py-8 text-gray-500">No pending accounts found.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Email</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Role</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Invited By</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Inviter Role</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Invited</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Expires</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Invite Link</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {accounts.map((account) => (
            <tr
              key={account.id}
              className={`hover:bg-gray-50 transition-colors duration-150 ${account.isExpired ? 'bg-red-50' : ''}`}
            >
              <td className="px-4 py-2">{account.email}</td>
              <td className="px-4 py-2 whitespace-nowrap">
                <span className={`px-2 py-1 rounded text-xs ${roleColors[account.role]}`}>
                  {roleLabels[account.role]}
                </span>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                {account.inviter ? account.inviter.name : <span className="text-gray-400 italic">Unknown</span>}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                {account.inviter ? (
                  <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">{account.inviter.role}</span>
                ) : (
                  <span className="text-gray-400 italic">-</span>
                )}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{formatDate(account.createdAt)}</td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{formatDate(account.expiresAt)}</td>
              <td className="px-4 py-2 text-sm text-gray-600">
                {account.token && !account.isExpired ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setLinkAccount(account)
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-blue-300 rounded text-blue-700 hover:bg-blue-50 transition-colors"
                    title="Show invite link"
                    aria-label={`Show invite link for ${account.email}`}
                  >
                    <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5" />
                    Show link
                  </button>
                ) : (
                  <span className="text-gray-400 italic">-</span>
                )}
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    account.isExpired ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {account.isExpired ? 'Expired' : 'Pending'}
                </span>
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {!account.isExpired && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onResendInvite(account.id)
                      }}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                      title="Resend invitation"
                      aria-label={`Resend invitation to ${account.email}`}
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(account)
                    }}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    title="Remove invitation"
                    aria-label={`Remove invitation for ${account.email}`}
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {linkAccount && linkAccount.token && (
        <Modal title="Invite link" small closeModal={() => setLinkAccount(null)}>
          <div className="mt-4 space-y-3">
            <p className="text-sm text-gray-600">
              Share this registration link with{' '}
              <span className="font-medium text-gray-900">{linkAccount.email}</span>. It expires on{' '}
              {formatDate(linkAccount.expiresAt)}.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={buildInviteUrl(linkAccount.token)}
                onFocus={(e) => e.currentTarget.select()}
                onClick={(e) => e.currentTarget.select()}
                className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
              <button
                onClick={() => handleCopyLink(linkAccount)}
                className="px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={copiedId === linkAccount.id ? faCheck : faCopy} className="h-4 w-4" />
                {copiedId === linkAccount.id ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
